/**
 * Order Service - API calls for order management
 * 
 * Backend endpoints expected:
 * GET    /api/orders              - Get user's orders (for logged-in user)
 * GET    /api/orders/:id          - Get single order
 * GET    /api/admin/orders        - Get all orders (admin)
 * PUT    /api/admin/orders/:id    - Update order status (admin)
 */

import api from './api';

// Development mode flag
const USE_MOCK = process.env.REACT_APP_USE_MOCK === 'true';

// Mock orders data
const MOCK_ORDERS = [
  {
    id: 'ORD-2026-001',
    userId: 2,
    userName: 'John Doe',
    userEmail: 'john@example.com',
    items: [
      { id: 1, name: 'Organic Tomatoes', quantity: 2, price: 120, image: 'https://images.unsplash.com/photo-1546470427-227c7369a9b0?w=100' },
      { id: 2, name: 'Fresh Spinach', quantity: 1, price: 80, image: 'https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=100' }
    ],
    subtotal: 320,
    shipping: 50,
    tax: 32,
    total: 402,
    status: 'delivered',
    paymentStatus: 'paid',
    paymentMethod: 'razorpay',
    shippingAddress: {
      name: 'John Doe',
      phone: '+91 98765 11111',
      address: '123 Main St',
      city: 'Mumbai',
      state: 'Maharashtra',
      pincode: '400001'
    },
    createdAt: '2026-02-28T10:30:00Z',
    updatedAt: '2026-03-01T14:00:00Z',
    deliveredAt: '2026-03-01T14:00:00Z'
  },
  {
    id: 'ORD-2026-002',
    userId: 2,
    userName: 'John Doe',
    userEmail: 'john@example.com',
    items: [
      { id: 3, name: 'Organic Honey', quantity: 1, price: 450, image: 'https://images.unsplash.com/photo-1587049352846-4a222e784d38?w=100' }
    ],
    subtotal: 450,
    shipping: 0,
    tax: 45,
    total: 495,
    status: 'shipped',
    paymentStatus: 'paid',
    paymentMethod: 'razorpay',
    shippingAddress: {
      name: 'John Doe',
      phone: '+91 98765 11111',
      address: '123 Main St',
      city: 'Mumbai',
      state: 'Maharashtra',
      pincode: '400001'
    },
    trackingNumber: 'TRK123456789',
    createdAt: '2026-03-02T09:00:00Z',
    updatedAt: '2026-03-03T08:00:00Z'
  },
  {
    id: 'ORD-2026-003',
    userId: 3,
    userName: 'Priya Sharma',
    userEmail: 'priya@example.com',
    items: [
      { id: 4, name: 'Farm Fresh Eggs', quantity: 2, price: 180, image: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=100' },
      { id: 5, name: 'Organic Milk', quantity: 3, price: 90, image: 'https://images.unsplash.com/photo-1563636619-e9143da7973b?w=100' }
    ],
    subtotal: 630,
    shipping: 50,
    tax: 63,
    total: 743,
    status: 'processing',
    paymentStatus: 'paid',
    paymentMethod: 'cod',
    shippingAddress: {
      name: 'Priya Sharma',
      phone: '+91 87654 22222',
      address: '456 Park Ave',
      city: 'Delhi',
      state: 'Delhi',
      pincode: '110001'
    },
    createdAt: '2026-03-03T06:30:00Z',
    updatedAt: '2026-03-03T06:30:00Z'
  },
  {
    id: 'ORD-2026-004',
    userId: 5,
    userName: 'Anita Gupta',
    userEmail: 'anita@example.com',
    items: [
      { id: 6, name: 'Organic Rice (5kg)', quantity: 1, price: 850, image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=100' },
      { id: 7, name: 'Mixed Vegetables Box', quantity: 1, price: 350, image: 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=100' }
    ],
    subtotal: 1200,
    shipping: 0,
    tax: 120,
    total: 1320,
    status: 'pending',
    paymentStatus: 'pending',
    paymentMethod: 'razorpay',
    shippingAddress: {
      name: 'Anita Gupta',
      phone: '+91 65432 44444',
      address: '321 Green Lane',
      city: 'Pune',
      state: 'Maharashtra',
      pincode: '411001'
    },
    createdAt: '2026-03-03T09:15:00Z',
    updatedAt: '2026-03-03T09:15:00Z'
  },
  {
    id: 'ORD-2026-005',
    userId: 2,
    userName: 'John Doe',
    userEmail: 'john@example.com',
    items: [
      { id: 8, name: 'Seasonal Fruit Basket', quantity: 1, price: 599, image: 'https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=100' }
    ],
    subtotal: 599,
    shipping: 50,
    tax: 60,
    total: 709,
    status: 'cancelled',
    paymentStatus: 'refunded',
    paymentMethod: 'razorpay',
    shippingAddress: {
      name: 'John Doe',
      phone: '+91 98765 11111',
      address: '123 Main St',
      city: 'Mumbai',
      state: 'Maharashtra',
      pincode: '400001'
    },
    createdAt: '2026-02-20T11:00:00Z',
    updatedAt: '2026-02-21T10:00:00Z',
    cancelledAt: '2026-02-21T10:00:00Z',
    cancelReason: 'Customer requested cancellation'
  }
];

// Transforms raw backend order response into the shape expected by the frontend components
const transformOrder = (raw) => {
  if (!raw) return null;

  // Parse shippingAddress string back into object
  // Format: "Name, Street, City, State - Pincode, Phone: 1234567890[, Alt: 9876543210]"
  let shippingAddress = raw.shippingAddress || '';
  let parsedAddress = { name: '', phone: '', address: '', city: '', state: '', pincode: '', alternatePhone: '' };
  try {
    const altMatch = shippingAddress.match(/Alt:\s*([0-9]+)/);
    if (altMatch) parsedAddress.alternatePhone = altMatch[1];
    const withoutAlt = shippingAddress.replace(/,?\s*Alt:.*$/, '').trim();
    const phoneMatch = withoutAlt.match(/Phone:\s*([0-9]+)/);
    if (phoneMatch) parsedAddress.phone = phoneMatch[1];
    const withoutPhone = withoutAlt.replace(/,?\s*Phone:.*$/, '').trim();
    // Builder (Checkout.jsx buildShippingAddress) always emits:
    // Name, <1-3 street/area parts>, City, State - Pincode — so the street
    // portion's length varies, but the first part (name) and last two parts
    // (city, "state - pincode") are fixed regardless of how many address
    // lines were filled in. Anchoring from both ends instead of a fixed
    // index keeps pincode/city correct even when addressLine2/area is set.
    const parts = withoutPhone.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length >= 1) parsedAddress.name = parts[0];
    if (parts.length >= 3) {
      const statePin = parts[parts.length - 1].split('-').map(p => p.trim());
      parsedAddress.state = statePin[0] || '';
      parsedAddress.pincode = statePin[1] || '';
      parsedAddress.city = parts[parts.length - 2];
      parsedAddress.address = parts.slice(1, parts.length - 2).join(', ');
    } else if (parts.length === 2) {
      parsedAddress.address = parts[1];
    }
  } catch (_) {}

  const items = (raw.items || []).map(item => ({
    id: item.productId,
    // Real order_items PK — needed to target amendment line changes.
    orderItemId: item.id,
    name: item.productName,
    quantity: item.quantity,
    price: parseFloat(item.priceAtPurchase) || 0,
    image: item.productImage || item.product?.images?.[0]?.imageUrl || '',
    itemNote: item.itemNote || null,
    // Amendment current-state mirror (BMF-BRD-2026-014).
    itemAmendmentStatus: item.itemAmendmentStatus || 'NONE',
    originalQuantity: item.originalQuantity ?? null,
    amendedQuantity: item.amendedQuantity ?? null,
    amendmentReasonCode: item.amendmentReasonCode || null,
  }));

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const total = parseFloat(raw.totalAmount) || subtotal;
  const shipping = total > subtotal ? parseFloat((total - subtotal).toFixed(2)) : 0;

  const isGuest = raw.isGuest || false;
  const userName = raw.user
    ? `${raw.user.firstName || ''} ${raw.user.lastName || ''}`.trim()
    : (raw.guestName || 'Guest');
  const userEmail = raw.user?.email || raw.guestEmail || '';
  const userPhone = raw.guestPhone || null;

  return {
    id: raw.id,
    userId: raw.userId,
    isGuest,
    userName,
    userEmail,
    userPhone,
    items,
    subtotal,
    shipping,
    tax: 0,
    total,
    status: raw.status,
    paymentStatus: raw.paymentStatus,
    paymentMethod: raw.paymentMethod || 'cod',
    shippingAddress: parsedAddress,
    orderNumber: raw.orderNumber || null,
    trackingNumber: raw.trackingNumber || null,
    deliveryType: raw.deliveryType || 'on_demand',
    deliverySlot: raw.deliverySlot || 'On-Demand',
    deliveryDate: raw.deliveryDate || null,
    deliveryInstruction: raw.deliveryInstruction || 'doorstep',
    assignedAgent: raw.assignment ? {
      agentId: raw.assignment.agentId,
      agentName: raw.assignment.agentName,
      agentPhone: raw.assignment.agentPhone,
    } : null,
    deliveryOtp: raw.deliveryOtp || null,
    deliveryOtpAttempts: raw.deliveryOtpAttempts || 0,
    deliveryOtpLocked: raw.deliveryOtpLocked || false,
    createdAt: raw.createdAt || null,
    orderNote: raw.orderNote || null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    deliveredAt: raw.deliveredAt || null,
    cancelledAt: raw.cancelledAt || null,
    cancelReason: raw.cancelReason || null,
    walletAmount: raw.walletAmount != null ? Number(raw.walletAmount) : null,
    onlineAmount: raw.onlineAmount != null ? Number(raw.onlineAmount) : null,
    paymentInstrument: raw.paymentInstrument || null,
    // Order amendment workflow (BMF-BRD-2026-014).
    amendmentStatus: raw.amendmentStatus || 'NONE',
    amendmentOriginalTotal: raw.amendmentOriginalTotal != null ? Number(raw.amendmentOriginalTotal) : null,
    amendmentRevisedTotal: raw.amendmentRevisedTotal != null ? Number(raw.amendmentRevisedTotal) : null,
    customerDecision: raw.customerDecision || null,
  };
};

const orderService = {
  /**
   * Place an order from the current cart
   * @param {Object} orderData - { shippingAddress, city, pincode, state }
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async placeOrder(orderData) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 500));
      const newOrder = {
        id: Date.now(),
        ...orderData,
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      return { success: true, data: newOrder };
    }
    
    try {
      const response = await api.post('/orders', {
        shippingAddress: orderData.shippingAddress,
        city: orderData.city,
        pincode: orderData.pincode,
        state: orderData.state,
        deliveryType: orderData.deliveryType || 'on_demand',
        deliverySlot: orderData.deliverySlot || 'On-Demand',
        deliveryDate: orderData.deliveryDate || null,
        deliveryFee: orderData.deliveryFee || 0,
        deliveryInstruction: orderData.deliveryInstruction || 'doorstep',
        orderNote: orderData.orderNote || null,
        itemNotes: orderData.itemNotes || undefined,
        productSubtotal: orderData.productSubtotal,
        discountAmount: orderData.discountAmount,
        discountType: orderData.discountType,
        shippingLatitude: orderData.shippingLatitude,
        shippingLongitude: orderData.shippingLongitude,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to place order' 
      };
    }
    
  },

  async placeGuestOrder(orderData) {
    try {
      const response = await api.post('/orders/guest', {
        sessionId: orderData.sessionId,
        guestName: orderData.guestName,
        guestEmail: orderData.guestEmail,
        guestPhone: orderData.guestPhone,
        ageConsentAcceptedAt: orderData.ageConsentAcceptedAt,
        shippingAddress: orderData.shippingAddress,
        city: orderData.city,
        pincode: orderData.pincode,
        state: orderData.state,
        deliveryType: orderData.deliveryType || 'on_demand',
        deliverySlot: orderData.deliverySlot || 'On-Demand',
        deliveryDate: orderData.deliveryDate || null,
        deliveryFee: orderData.deliveryFee || 0,
        deliveryInstruction: orderData.deliveryInstruction || 'doorstep',
        orderNote: orderData.orderNote || null,
        itemNotes: orderData.itemNotes || undefined,
        productSubtotal: orderData.productSubtotal,
        discountAmount: orderData.discountAmount,
        discountType: orderData.discountType,
        shippingLatitude: orderData.shippingLatitude,
        shippingLongitude: orderData.shippingLongitude,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to place order' 
      };
    }
  },

  async trackOrder(token) {
    try {
      const response = await api.get(`/orders/track/${token}`);
      return { success: true, data: transformOrder(response.data) };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Order not found' };
    }
  },

  async getRecentGuestOrders(sessionId) {
    try {
      const response = await api.get('/orders/recent', { params: { sessionId } });
      return { 
        success: true, 
        data: (response.data || []).map(transformOrder) 
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Get orders for current user
   */
  async getMyOrders({ page = 1, limit = 10, status = '' } = {}) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // In mock mode, return orders for user ID 2 (John Doe)
      let orders = MOCK_ORDERS.filter(o => o.userId === 2);
      
      if (status) {
        orders = orders.filter(o => o.status === status);
      }
      
      orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      const total = orders.length;
      const start = (page - 1) * limit;
      const paginatedOrders = orders.slice(start, start + limit);
      
      return {
        success: true,
        data: {
          orders: paginatedOrders,
          pagination: { page, limit, total, pages: Math.ceil(total / limit) }
        }
      };
    }
    
    try {
      const response = await api.get('/orders', { params: { page, limit, status } });
      const transformed = {
        ...response.data,
        orders: (response.data.orders || []).map(transformOrder),
      };
      return { success: true, data: transformed };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Get single order by ID
   */
  async getOrder(id) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 200));
      const order = MOCK_ORDERS.find(o => o.id === id);
      if (!order) {
        return { success: false, error: 'Order not found' };
      }
      return { success: true, data: order };
    }
    
    try {
      const response = await api.get(`/orders/${id}`);
      return { success: true, data: transformOrder(response.data) };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Cancel an order
   * @param {number|string} id - Order ID
   * @param {string} reason - Optional cancellation reason
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async cancelOrder(id, reason = '', refundTo = undefined) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 300));
      const order = MOCK_ORDERS.find(o => o.id === id);
      if (!order) {
        return { success: false, error: 'Order not found' };
      }
      if (!['pending', 'confirmed', 'processing'].includes(order.status)) {
        return { success: false, error: `Cannot cancel order with status "${order.status}"` };
      }
      order.status = 'cancelled';
      order.cancelledAt = new Date().toISOString();
      order.cancelReason = reason;
      return { success: true, data: order };
    }

    try {
      const payload = { reason };
      if (refundTo) payload.refundTo = refundTo;
      const response = await api.patch(`/orders/${id}/cancel`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to cancel order'
      };
    }
  },

  // ── Order amendment workflow (BMF-BRD-2026-014) ────────────────────────────

  /**
   * Admin: propose an order amendment.
   * items = [{ orderItemId, action: 'remove'|'reduce', proposedQuantity, reasonCode, note }]
   */
  async createAmendment(orderId, items) {
    try {
      const response = await api.post(`/admin/orders/${orderId}/amendment`, { items });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to create amendment' };
    }
  },

  /** Fetch the pending amendment (request + per-line diff) for an order. */
  async getAmendment(orderId) {
    try {
      const response = await api.get(`/orders/${orderId}/amendment`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to load amendment' };
    }
  },

  /** Customer: approve the pending amendment. */
  async approveAmendment(orderId, refundTo = undefined) {
    try {
      const payload = refundTo ? { refundTo } : {};
      const response = await api.patch(`/orders/${orderId}/amendment/approve`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to approve amendment' };
    }
  },

  /** Customer: cancel the whole order from the approval screen. */
  async cancelAmendmentOrder(orderId, refundTo = undefined) {
    try {
      const payload = refundTo ? { refundTo } : {};
      const response = await api.patch(`/orders/${orderId}/amendment/cancel`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to cancel order' };
    }
  },

  /**
   * Get all orders (admin only)
   */
  async getAllOrders({ page = 1, limit = 10, status = '', search = '', deliveryDate = '', deliveryType = '', deliverySlot = '', pincode = '' } = {}) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 300));
      
      let orders = [...MOCK_ORDERS];
      
      if (status) {
        orders = orders.filter(o => o.status === status);
      }
      
      if (search) {
        const s = search.toLowerCase();
        orders = orders.filter(o => 
          o.id.toLowerCase().includes(s) ||
          o.userName.toLowerCase().includes(s) ||
          o.userEmail.toLowerCase().includes(s)
        );
      }
      
      orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      const total = orders.length;
      const start = (page - 1) * limit;
      const paginatedOrders = orders.slice(start, start + limit);
      
      return {
        success: true,
        data: {
          orders: paginatedOrders,
          pagination: { page, limit, total, pages: Math.ceil(total / limit) }
        }
      };
    }
    
    try {
      const response = await api.get('/admin/orders', { params: { page, limit, status, search, ...(deliveryDate && { deliveryDate }), ...(deliveryType && { deliveryType }), ...(deliverySlot && { deliverySlot }), ...(pincode && { pincode }) } });
      const transformed = {
        ...response.data,
        orders: (response.data.orders || []).map(transformOrder),
      };
      return { success: true, data: transformed };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Update order status (admin only)
   */
  async updateOrderStatus(id, status) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 300));
      const order = MOCK_ORDERS.find(o => o.id === id);
      if (!order) {
        return { success: false, error: 'Order not found' };
      }
      order.status = status;
      order.updatedAt = new Date().toISOString();
      
      if (status === 'delivered') {
        order.deliveredAt = new Date().toISOString();
      }
      if (status === 'cancelled') {
        order.cancelledAt = new Date().toISOString();
      }
      
      return { success: true, data: order };
    }
    
    try {
      const response = await api.put(`/admin/orders/${id}`, { status });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Admin force-deliver — marks an order delivered without OTP verification.
   * Use only when customer can't relay the OTP or agent is locked out after
   * 5 wrong attempts. Action is logged against the admin user via order note.
   */
  async forceDeliver(id, reason) {
    try {
      const response = await api.post(`/admin/orders/${id}/force-deliver`, { reason });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  /**
   * Get order statistics (admin)
   */
  async getStats() {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const total = MOCK_ORDERS.length;
      const pending = MOCK_ORDERS.filter(o => o.status === 'pending').length;
      const processing = MOCK_ORDERS.filter(o => o.status === 'processing').length;
      const shipped = MOCK_ORDERS.filter(o => o.status === 'shipped').length;
      const delivered = MOCK_ORDERS.filter(o => o.status === 'delivered').length;
      const cancelled = MOCK_ORDERS.filter(o => o.status === 'cancelled').length;
      const revenue = MOCK_ORDERS
        .filter(o => o.paymentStatus === 'paid')
        .reduce((sum, o) => sum + o.total, 0);
      
      return {
        success: true,
        data: { total, pending, processing, shipped, delivered, cancelled, revenue }
      };
    }
    
    try {
      const response = await api.get('/admin/orders/stats');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Admin — Refund Request Management
   */
  async getRefundRequests(status = '') {
    try {
      const params = status ? { status } : {};
      const response = await api.get('/orders/admin/refund-requests', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  async approveRefundRequest(id) {
    try {
      const response = await api.patch(`/orders/admin/refund-requests/${id}/approve`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  async rejectRefundRequest(id, reason) {
    try {
      const response = await api.patch(`/orders/admin/refund-requests/${id}/reject`, { reason });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  /**
   * Download invoice PDF for an order
   * @param {number|string} id - Order ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async downloadInvoice(id) {
    try {
      const response = await api.get(`/orders/${id}/invoice`, {
        responseType: 'blob',
        timeout: 60000, // 60 second timeout for PDF generation
      });
      
      // Check if response is actually an error (JSON error response as blob)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        return { success: false, error: errorData.message || 'Failed to download invoice' };
      }
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Download invoice error:', error);
      
      // Handle blob error response
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          return { success: false, error: errorData.message || 'Failed to download invoice' };
        } catch {
          return { success: false, error: 'Failed to download invoice' };
        }
      }
      
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to download invoice'
      };
    }
  },

  /**
   * Download the warehouse picking list PDF for an order (On-Demand orders).
   * Black-and-white A4 sheet with Check / Product Name / Unit Type / Quantity.
   * @param {number|string} id - Order ID
   * @param {string} [orderNumber] - Used for a friendlier download filename
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async downloadPickingList(id, orderNumber) {
    try {
      const response = await api.get(`/orders/${id}/picking-list`, {
        responseType: 'blob',
        timeout: 60000,
      });

      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        return { success: false, error: errorData.message || 'Failed to download list' };
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `picking-list-${orderNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Download picking list error:', error);

      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          return { success: false, error: errorData.message || 'Failed to download list' };
        } catch {
          return { success: false, error: 'Failed to download list' };
        }
      }

      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to download list',
      };
    }
  },

  /**
   * Admin — scheduled-order pick lists whose slot cutoff has passed and are
   * ready to download. One entry per ready (date + slot), only when it has at
   * least one live order. Drives the admin "list ready" alerts.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  async getReadyScheduledLists() {
    try {
      const response = await api.get('/orders/scheduled-lists/ready');
      return { success: true, data: response.data || [] };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  /**
   * Admin — download the aggregated scheduled picking list PDF for one slot.
   * @param {string} date - Delivery date (YYYY-MM-DD)
   * @param {string} slot - Slot snapshot string (e.g. "Evening (5pm - 8pm)")
   * @param {string} [label] - Slot label, used only for a friendlier filename
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async downloadScheduledList({ date, slot, label, orderIds }) {
    try {
      const response = await api.get('/orders/scheduled-lists/pdf', {
        params: { date, slot, orderIds },
        paramsSerializer: { indexes: null },
        responseType: 'blob',
        timeout: 60000,
      });

      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        return { success: false, error: errorData.message || 'Failed to download list' };
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeLabel = (label || 'slot').replace(/[^a-z0-9]+/gi, '-').toLowerCase();

      //link.download = `scheduled-list-${date}-${safeLabel}.pdf`;
      link.download = `${date ? `scheduled-list-${date}` : 'scheduled-list'}-${safeLabel}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Download scheduled list error:', error);

      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          return { success: false, error: errorData.message || 'Failed to download list' };
        } catch {
          return { success: false, error: 'Failed to download list' };
        }
      }

      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to download list',
      };
    }
  }
};

export default orderService;